import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { featureFlags } from 'environments/environment';
import { firstValueFrom, interval, Subscription } from 'rxjs';

import { WINDOW } from '../../../core/window.token';
import { SessionExtendModalComponent } from '../../components/session-extend-modal/session-extend-modal.component';
import { LoggerService } from '../logger/logger.service';
import { UserSessionApiService } from '../user-session-api/user-session-api.service';
import { UserDataService } from '../userdata/userdata.service';

@Injectable({
  providedIn: 'root'
})
export class UserSessionMonitoringService {
  private window = inject(WINDOW);
  private userSessionApiService = inject(UserSessionApiService);
  private dynamicMonitoringSubscription?: Subscription;
  private userDataService = inject(UserDataService);
  private dialog = inject(MatDialog);
  private logger = inject(LoggerService).setContext('UserSessionMonitoringService');

  private sessionExpiryTime = 0;
  private sessionInterval = 0;
  private logoutTimeout?: ReturnType<typeof setTimeout>;
  private activeModalRef?: MatDialogRef<SessionExtendModalComponent>;
  private sessionCheckRetryCount = 0;
  private readonly MAX_RETRY_ATTEMPTS = 3;

  startPeriodicMonitoring(): void {
    if (!featureFlags.enableUserSessionMonitoring) {
      this.logger.info('User session monitoring disabled via feature flag');
      return;
    }

    // Start the session check immediately and then on dynamic intervals
    this.sessionCheck();
  }

  stopMonitoring(): void {
    this.stopScheduler();
    if (this.logoutTimeout) {
      clearTimeout(this.logoutTimeout);
      this.logoutTimeout = undefined;
    }
    if (this.activeModalRef) {
      this.activeModalRef.close();
      this.activeModalRef = undefined;
    }
  }

  private stopScheduler(): void {
    if (this.dynamicMonitoringSubscription) {
      this.dynamicMonitoringSubscription.unsubscribe();
      this.dynamicMonitoringSubscription = undefined;
    }
  }

  private startScheduler(schedulerTime: number): void {
    // Clear any existing scheduler
    this.stopScheduler();

    this.dynamicMonitoringSubscription = interval(schedulerTime).subscribe(() => {
      this.sessionCheck();
    });
  }

  private async sessionCheck(isRetry = false): Promise<void> {
    const SIX_MINUTES_MS = 360000;
    const THREE_MINUTES_MS = 180000;
    try {
      let response;
      try {
        response = await firstValueFrom(this.userSessionApiService.getSessionExpiryDetails());
      } catch (error) {
        if (!isRetry) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return this.sessionCheck(true);
        }
        throw error;
      }

      if (!response) {
        if (!isRetry) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return this.sessionCheck(true);
        }
        this.logger.error('No response from session expiry check');
        this.stopMonitoring();
        return;
      }

      // Reset retry counter on successful response
      this.sessionCheckRetryCount = 0;

      this.sessionExpiryTime = response.sessionExpiryTime;

      // Stop existing scheduler before starting a new one
      this.stopScheduler();

      if (!response.isFinalSessionExtensionOver) {
        // Normal session monitoring
        if (this.sessionExpiryTime > SIX_MINUTES_MS) {
          // > 6 minutes
          this.sessionInterval = THREE_MINUTES_MS; // Check every 3 minutes
          this.startScheduler(this.sessionInterval);
        } else if (
          this.sessionExpiryTime > THREE_MINUTES_MS &&
          this.sessionExpiryTime <= SIX_MINUTES_MS
        ) {
          // 3-6 minutes
          this.sessionInterval = this.sessionExpiryTime - THREE_MINUTES_MS;
          this.startScheduler(this.sessionInterval);
        } else {
          // Less than 3 minutes - SHOW SESSION EXTENSION MODAL
          this.logger.info('Session expiring soon, showing extension modal');
          this.showSessionExtendModal();
          // Modal component handles the timeout internally
        }
      } else {
        // Final session extension is over - just schedule logout, no modal needed
        if (this.sessionExpiryTime > SIX_MINUTES_MS) {
          this.sessionInterval = THREE_MINUTES_MS;
          this.startScheduler(this.sessionInterval);
        } else if (
          this.sessionExpiryTime > THREE_MINUTES_MS &&
          this.sessionExpiryTime <= SIX_MINUTES_MS
        ) {
          this.sessionInterval = this.sessionExpiryTime - THREE_MINUTES_MS;
          this.startScheduler(this.sessionInterval);
        } else {
          // Less than 3 minutes and final extension over - auto logout when time expires
          this.logoutTimeout = setTimeout(() => {
            this.fullLogout();
          }, this.sessionExpiryTime);
        }
      }
    } catch (error: any) {
      // Handle 401 (session expired)
      if (error.status === 401) {
        this.logger.warn('Session check: Got 401, stopping scheduler and checking auth');
        this.stopMonitoring();

        // Check retry limit before calling checkAccountStatus
        if (this.sessionCheckRetryCount < this.MAX_RETRY_ATTEMPTS) {
          this.sessionCheckRetryCount++;
          this.logger.info(
            `Attempting account status check (attempt ${this.sessionCheckRetryCount}/${this.MAX_RETRY_ATTEMPTS})`
          );
          await this.checkAccountStatus();
        } else {
          this.logger.error('Max retry attempts reached for session check, stopping monitoring');
          this.sessionCheckRetryCount = 0;
        }
        return;
      }

      // Handle any other error
      this.logger.error('Session check error:', error);
      this.stopMonitoring();
      this.sessionCheckRetryCount = 0;
    }
  }

  private fullLogout(): void {
    const returnUrl = this.window.location.href;
    this.userDataService.clearUserData();
    this.userSessionApiService.logout(returnUrl);
  }

  private showSessionExtendModal(): void {
    // Close any existing modal
    if (this.activeModalRef) {
      this.activeModalRef.close();
    }

    this.activeModalRef = this.dialog.open(SessionExtendModalComponent, {
      data: { timeRemaining: this.sessionExpiryTime },
      disableClose: true, // Prevent closing by clicking outside
      width: '560px',
      panelClass: 'session-extend-dialog'
    });

    this.activeModalRef.afterClosed().subscribe(async (result) => {
      // Clear the logout timeout since user took action or time expired
      if (this.logoutTimeout) {
        clearTimeout(this.logoutTimeout);
        this.logoutTimeout = undefined;
      }

      if (result === 'extend') {
        // Call the extend session endpoint
        const extended = await firstValueFrom(this.userSessionApiService.extendSession());
        if (extended) {
          // Check session again to get new expiry time
          await this.sessionCheck();
        } else {
          this.logger.error('Failed to extend session');
          await this.checkAccountStatus();
        }
      } else if (result === 'signout' || result === 'timeout') {
        // Call logout endpoint with current URL as return URL
        this.fullLogout();
      }
      this.activeModalRef = undefined;
    });
  }

  private async checkAccountStatus(): Promise<void> {
    const currentUrl = this.window.location.href;

    try {
      const response = await firstValueFrom(
        this.userSessionApiService.verifyAccountStatus(currentUrl)
      );

      if (!response) {
        this.logger.error('No response from account status check');
        this.sessionCheckRetryCount = 0;
        return;
      }

      if (response.status === 'NOT_LOGGED_IN') {
        this.sessionCheckRetryCount = 0;
        this.userDataService.clearUserData();
        this.window.location.href = response.redirectUrl;
      } else {
        // Resume session monitoring only if we haven't exceeded retry limit
        if (this.sessionCheckRetryCount < this.MAX_RETRY_ATTEMPTS) {
          await this.sessionCheck();
        } else {
          this.logger.error('Max retry attempts reached, stopping session monitoring');
          this.sessionCheckRetryCount = 0;
          this.stopMonitoring();
        }
      }
    } catch (error) {
      this.logger.error('Session monitoring error:', error);
      this.sessionCheckRetryCount = 0;
      this.stopMonitoring();
    }
  }
}
